/**
 * StoragePage — iter859. Admin view of Emergent Object Storage stats.
 *
 * Turns the previously invisible /app/uploads/ → cloud migration into a
 * visible operations tile: cloud health, disk cache size + file count,
 * per-category breakdown, and orphan detection.
 */
import { useEffect, useState } from 'react';
import { HardDrive, Cloud, CloudOff, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

const fmtBytes = (n) => {
  if (!n || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export default function StoragePage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/admin/storage/stats');
      setStats(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load storage stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-muted-foreground" data-testid="storage-loading">
        Loading storage stats…
      </div>
    );
  }

  const orphans = stats?.orphans_sampled_total || 0;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="storage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <HardDrive size={26} className="text-primary" />
            Persistent Storage
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Uploaded files are stored in Emergent Object Storage and mirrored to a fast local cache. Files survive container restarts.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="storage-refresh-btn"
        >
          <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="storage-cloud-card">
          <div className="flex items-center gap-2 mb-2">
            {stats?.cloud_enabled ? (
              <Cloud size={18} className="text-emerald-600" />
            ) : (
              <CloudOff size={18} className="text-amber-500" />
            )}
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Cloud</span>
          </div>
          <p className="text-2xl font-bold" data-testid="storage-cloud-status">
            {stats?.cloud_enabled ? 'Active' : 'Offline'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{stats?.app_name || '—'}</p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <HardDrive size={18} className="text-indigo-600" />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Disk cache</span>
          </div>
          <p className="text-2xl font-bold tabular-nums" data-testid="storage-disk-bytes">
            {fmtBytes(stats?.disk_cache_bytes)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{stats?.disk_cache_files ?? 0} file(s)</p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={18} className="text-emerald-600" />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">DB references</span>
          </div>
          <p className="text-2xl font-bold tabular-nums" data-testid="storage-db-refs">
            {stats?.db_references_total ?? 0}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">rows pointing at uploads</p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className={orphans > 0 ? 'text-amber-500' : 'text-muted-foreground'} />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Orphans (sample)</span>
          </div>
          <p className={`text-2xl font-bold tabular-nums ${orphans > 0 ? 'text-amber-600' : ''}`} data-testid="storage-orphans">
            {orphans}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {orphans > 0 ? 'Files missing — re-upload heals' : 'All references resolved'}
          </p>
        </Card>
      </div>

      {stats?.legacy_local_documents?.length > 0 && (
        <Card className="p-4" data-testid="storage-legacy-docs">
          <h2 className="text-lg font-semibold mb-1">Older document files (read-only check)</h2>
          <p className="text-xs text-muted-foreground mb-3">Records saved with an old local path. Nothing is changed here.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
              <th className="text-left py-1">File</th><th className="text-left">Path</th><th className="text-left">On disk</th><th className="text-left">In cloud</th>
            </tr></thead>
            <tbody>{stats.legacy_local_documents.map((d) => (
              <tr key={d.id} className="border-b last:border-b-0" data-testid={`storage-legacy-${d.id}`}>
                <td className="py-1">{d.file_name || '—'}</td><td className="text-xs text-muted-foreground">{d.file_path}</td>
                <td>{d.exists_on_disk ? 'Yes' : 'No'}</td><td>{d.exists_in_cloud ? 'Yes' : 'No'}</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-3">By category</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                <th className="text-left py-2 font-medium">Subfolder</th>
                <th className="text-right py-2 font-medium">Files (disk)</th>
                <th className="text-right py-2 font-medium">Bytes</th>
                <th className="text-right py-2 font-medium">DB refs</th>
                <th className="text-right py-2 font-medium">Orphans</th>
              </tr>
            </thead>
            <tbody data-testid="storage-per-subfolder">
              {(stats?.per_subfolder || []).map((row) => (
                <tr key={row.subfolder} className="border-b last:border-b-0" data-testid={`storage-row-${row.subfolder}`}>
                  <td className="py-2 font-mono text-xs">{row.subfolder}</td>
                  <td className="py-2 text-right tabular-nums">{row.disk_files}</td>
                  <td className="py-2 text-right tabular-nums">{fmtBytes(row.disk_bytes)}</td>
                  <td className="py-2 text-right tabular-nums">{row.db_references}</td>
                  <td className="py-2 text-right">
                    {row.orphans_sampled > 0 ? (
                      <Badge variant="destructive">{row.orphans_sampled}</Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
          {stats?.note}
        </p>
      </Card>
    </div>
  );
}
